import { Mail, ShieldCheck, Camera, Archive, Check } from "lucide-react";

/**
 * 3-paths converging animated diagram — the heart of the Ticketify story.
 * Used on both Landing and HowItWorks pages.
 */
export default function ThreeWaysDiagram({ className = "" }) {
    return (
        <div
            className={`relative tk-card p-6 sm:p-10 overflow-hidden ${className}`}
            data-testid="three-ways-diagram"
        >
            {/* Ambient gradient */}
            <div
                className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-[#007AFF]/12 to-[#34C759]/12 blur-3xl rounded-full"
                aria-hidden
            />
            <div
                className="absolute -bottom-24 -left-24 w-96 h-96 bg-gradient-to-tr from-[#34C759]/10 to-transparent blur-3xl rounded-full"
                aria-hidden
            />

            <div className="relative grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-stretch">
                {/* LEFT — 3 input sources */}
                <div className="space-y-4">
                    <SourceCard
                        icon={Mail}
                        kicker="Voie 1 · Tout seul"
                        title="Reçu par email"
                        body="Amazon, SNCF, Uber, Apple, Stripe… Le mail arrive, on prend le ticket à l'intérieur."
                        delay={0}
                        accent="#007AFF"
                    />
                    <SourceCard
                        icon={ShieldCheck}
                        kicker="Voie 2 · Tout seul"
                        title="Ticket de caisse digital"
                        body="Carrefour, Monoprix, Decathlon, Fnac, IKEA… Vous présentez la carte fidélité, le ticket vous est envoyé par mail."
                        delay={150}
                        accent="#34C759"
                    />
                    <SourceCard
                        icon={Camera}
                        kicker="Voie 3 · 7 secondes"
                        title="Photo du ticket papier"
                        body="Boulanger, bar, pharmacie, petit commerce. Un coup de photo et c'est fini — on lit le ticket à votre place."
                        delay={300}
                        accent="#FF9F0A"
                    />
                </div>

                {/* MIDDLE — animated SVG connectors (desktop only) */}
                <div className="hidden lg:flex flex-col items-center justify-center relative">
                    <svg viewBox="0 0 80 360" className="h-full w-20" aria-hidden>
                        <defs>
                            <linearGradient id="ttkLineGradient" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0" stopColor="#007AFF" stopOpacity="0.0" />
                                <stop offset="0.5" stopColor="#007AFF" stopOpacity="0.7" />
                                <stop offset="1" stopColor="#34C759" stopOpacity="0.0" />
                            </linearGradient>
                            <radialGradient id="ttkDotGlow">
                                <stop offset="0%" stopColor="#34C759" stopOpacity="1" />
                                <stop offset="100%" stopColor="#34C759" stopOpacity="0" />
                            </radialGradient>
                        </defs>
                        {[60, 180, 300].map((y, i) => (
                            <g key={`flow-${y}`}>
                                <path
                                    d={`M0 ${y} C 30 ${y}, 30 180, 80 180`}
                                    stroke="url(#ttkLineGradient)"
                                    strokeWidth="2"
                                    fill="none"
                                    className="tk-flow-path"
                                    style={{ animationDelay: `${i * 0.6}s` }}
                                />
                                <circle r="3.5" fill="#007AFF" className="tk-flow-dot">
                                    <animateMotion
                                        dur="2.6s"
                                        repeatCount="indefinite"
                                        begin={`${i * 0.6}s`}
                                        path={`M0 ${y} C 30 ${y}, 30 180, 80 180`}
                                    />
                                </circle>
                            </g>
                        ))}
                    </svg>
                </div>

                {/* RIGHT — the vault */}
                <div className="flex items-center justify-center">
                    <div className="relative w-full">
                        <div
                            className="absolute -inset-6 bg-gradient-to-br from-[#007AFF]/20 to-[#34C759]/20 blur-2xl rounded-full"
                            aria-hidden
                        />
                        <div className="relative tk-card p-6 sm:p-7 text-center">
                            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#34C759] flex items-center justify-center mb-4 tk-vault-pulse">
                                <Archive className="w-7 h-7 text-white" />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">
                                Au même endroit
                            </p>
                            <h3 className="mt-1.5 text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                                Votre dossier
                            </h3>
                            <p className="mt-2 text-sm text-[#86868B] leading-relaxed">
                                Chaque ticket est collé à la bonne dépense, gardé en sécurité en France, prêt à donner au comptable.
                            </p>
                            <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#34C759]/15 text-[#34C759]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] tk-live-dot" />
                                En sécurité · 10 ans
                            </div>
                            <div className="mt-5 grid grid-cols-3 gap-1.5">
                                {["TVA", "PDF", "Match"].map((t) => (
                                    <div
                                        key={t}
                                        className="text-[10px] font-semibold uppercase tracking-wider py-1.5 rounded-lg bg-[#F5F5F7] border border-black/5 text-[#1D1D1F]"
                                    >
                                        <Check className="w-3 h-3 text-[#34C759] inline mr-1" />
                                        {t}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SourceCard({ icon: Icon, kicker, title, body, delay = 0, accent = "#007AFF" }) {
    return (
        <div
            className="tk-card p-5 sm:p-6 tk-rise"
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="flex items-center gap-3 mb-2.5">
                <div
                    className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${accent}18` }}
                >
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                </div>
                <div>
                    <p
                        className="text-[10px] font-bold uppercase tracking-[0.15em]"
                        style={{ color: accent }}
                    >
                        {kicker}
                    </p>
                    <h4 className="text-base font-semibold text-[#1D1D1F]">{title}</h4>
                </div>
            </div>
            <p className="text-sm text-[#86868B] leading-relaxed">{body}</p>
        </div>
    );
}
